import {
  b64Vecs,
  getIndexBetween,
  TLDrawShapeSegment,
  TLShapeId,
  Vec,
  VecModel,
} from "tldraw";
import { asColor } from "../../shared/format/FocusedColor";
import { HighlightAction } from "../../shared/schema/AgentActionSchemas";
import { Streaming } from "../../shared/types/Streaming";
import { AgentHelpers } from "../AgentHelpers";
import { AgentActionUtil, registerActionUtil } from "./AgentActionUtil";
import {
  AUTHORED_SOURCE,
  isAuthoredContentAction,
} from "../utils/authoredContent";
import { annotationMeta } from "../utils/annotationContent";

export const HighlightActionUtil = registerActionUtil(
  class HighlightActionUtil extends AgentActionUtil<HighlightAction> {
    static override type = "highlight" as const;

    override getInfo(action: Streaming<HighlightAction>) {
      return {
        icon: "pencil" as const,
        description: action.intent ?? "",
      };
    }

    override sanitizeAction(
      action: Streaming<HighlightAction>,
      helpers: AgentHelpers,
    ) {
      if (!action.points) return action;

      action.shapeId = helpers.ensureShapeIdIsUnique(action.shapeId);

      const validPoints = action.points
        .map((point) => helpers.ensureValueIsVec(point))
        .filter((v) => v !== null);

      action.points = validPoints;

      return action;
    }

    override applyAction(action: Streaming<HighlightAction>) {
      if (!action.points) return;
      if (action.points.length === 0) return;
      if (!action.shapeId) return;

      const shapeId = `shape:${action.shapeId}` as TLShapeId;

      if (action.points.length === 1) {
        action.points.push({ ...action.points[0] });
      }

      const minX = Math.min(...action.points.map((p) => p.x));
      const minY = Math.min(...action.points.map((p) => p.y));

      const points: VecModel[] = [];
      const maxDistanceBetweenPoints = action.style === "smooth" ? 10 : 2;
      for (let i = 0; i < action.points.length - 1; i++) {
        const point = action.points[i];
        points.push(point);

        const nextPoint = action.points[i + 1];
        if (!nextPoint) continue;

        const distance = Vec.Dist(point, nextPoint);
        const numPointsToAdd = Math.floor(distance / maxDistanceBetweenPoints);
        const pointsToAdd = Array.from({ length: numPointsToAdd }, (_, j) => {
          const t = (j + 1) / (numPointsToAdd + 1);
          return Vec.Lrp(point, nextPoint, t);
        });
        points.push(...pointsToAdd);
      }

      if (points.length === 0) return;
      if (points.length === 1) {
        points.push({ ...points[0] });
      }

      const segmentPoints = points.map((point) => ({
        x: point.x - minX,
        y: point.y - minY,
        z: 0.75,
      }));
      const base64Points = b64Vecs.encodePoints(segmentPoints);

      const isAuthored = isAuthoredContentAction(action);

      const segments: TLDrawShapeSegment[] = [
        {
          type: "free",
          path: base64Points,
        },
      ];

      this.editor.createShape({
        id: shapeId,
        type: "highlight",
        x: minX,
        y: minY,
        rotation: action.rotation ?? 0,
        // Semi-transparent so typeset content stays readable underneath.
        opacity: action.opacity ?? 0.4,
        // Same authored-content check as Create/Pen, so the answer lives in one
        // place; the seed expansion emits no highlights today.
        isLocked: isAuthored,
        // AI authorship stamp — see CreateActionUtil.
        meta: {
          role: "agent",
          // Asked, not assumed: an authored highlight is problem content, and
          // tagging it would strip the problem statement from the screenshot.
          ...annotationMeta(action),
          ...(isAuthored ? { source: AUTHORED_SOURCE } : {}),
        },
        props: {
          color: asColor(action.color ?? "yellow"),
          size: action.size ?? "s",
          segments,
          isComplete: true,
          isPen: true,
        },
      });
      const highlight = this.editor.getShape(shapeId);
      const bounds = this.editor.getShapePageBounds(shapeId);
      if (!highlight || !bounds) return;
      const imageSiblings = new Set(
        this.editor.getCurrentPageShapes().flatMap((shape) => {
          if (shape.type !== "image") return [];
          const imageBounds = this.editor.getShapeMaskedPageBounds(shape);
          if (!imageBounds || !bounds.collides(imageBounds)) return [];
          const sibling = [shape, ...this.editor.getShapeAncestors(shape)].find(
            (ancestor) => ancestor.parentId === highlight.parentId,
          );
          return sibling ? [sibling.id] : [];
        }),
      );
      const siblings = this.editor
        .getSortedChildIdsForParent(highlight.parentId)
        .filter((id) => id !== shapeId)
        .map((id) => this.editor.getShape(id)!);
      let imageIndex = -1;
      siblings.forEach((shape, index) => {
        if (imageSiblings.has(shape.id)) imageIndex = index;
      });
      // The lowest visible layer is above overlapping images, which may be opaque.
      const index = getIndexBetween(
        siblings[imageIndex]?.index,
        siblings[imageIndex + 1]?.index,
      );
      this.editor.run(
        () =>
          this.editor.updateShape({ id: shapeId, type: "highlight", index }),
        {
          ignoreShapeLock: true,
        },
      );
    }
  },
);
